import type { JSX } from 'react';
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';

export type ButtonVariant = 'primary' | 'action' | 'ghost' | 'soft' | 'indigo' | 'danger' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onClick'> {
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /**
   * primary — оранжевая главная (одна на экран); action — синяя (действие CRM,
   * «Экспорт»); ghost — контурная; soft — оранжевая подложка; indigo — на
   * промо/тёмных карточках; danger — удаление; text — без рамки.
   */
  variant?: ButtonVariant;
  /** sm 32 px · md 38 px · lg 48 px (крупные цели на телефоне). */
  size?: ButtonSize;
  icon?: ReactNode;
  /** Иконка справа (стрелка «→», «▾»). */
  iconRight?: ReactNode;
  loading?: boolean;
  /** На всю ширину контейнера. */
  block?: boolean;
  /** Внутренняя ссылка (react-router) — рендерится <Link> с видом кнопки. */
  to?: string;
  /** Внешняя ссылка — <a target=_blank>. */
  href?: string;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Кнопка дизайн-системы HUB 2.0 (порт `_ds/components/Button`): радиус 6,
 * 600, hover — без «прыжков» (только цвет). Для форм antd по-прежнему можно
 * брать antd `Button` (тема уже оранжевая), этот — для шапок, карточек, CTA.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading,
  block,
  to,
  href,
  type = 'button',
  className,
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps): JSX.Element {
  const cls = [
    'fk-ds-btn',
    `fk-ds-btn--${variant}`,
    `fk-ds-btn--${size}`,
    block ? 'fk-ds-btn--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const inner = (
    <>
      {loading ? <LoadingOutlined /> : icon}
      {children}
      {iconRight}
    </>
  );
  if (to && !disabled) {
    return (
      <Link to={to} className={cls} style={rest.style} title={rest.title} onClick={onClick}>
        {inner}
      </Link>
    );
  }
  if (href && !disabled) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={rest.style} title={rest.title}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} className={cls} disabled={disabled || loading} onClick={onClick} {...rest}>
      {inner}
    </button>
  );
}
